<template>
  <div v-if="hasProducts" :id="textToTarget(title, 'heading-2')" class="w-full">
    <ProductCardCollection
      :block-position
      :cta="ctaLink || undefined"
      :layout
      :product-granularity="productGranularity || undefined"
      :products="products || []"
      provider="algolia"
      :subtitle="subtitle || ''"
      :title="title || ''"
      :tracking="{
        productComponent: 'RefinedProducts',
        ...tracking,
      }"
    />
  </div>
  <CardCollectionSkeleton v-else-if="pending" :layout :with-title="!!title" />
</template>

<script setup lang="ts">
import { computed, inject, watch } from 'vue'

import type { RefinedProductsApiProps } from '@backmarket/http-api/src/api-specs-content/models/refined-products'
import ProductCardCollection from '@backmarket/nuxt-layer-recommendation/ProductCardCollection.vue'
import { useAddProductsCacheTagHeader } from '@backmarket/nuxt-module-http-cache/composables/useAddCacheTagHeader'

import type { BlockProps } from '../../models/content-block'
import { useSearchProducts } from '../../products/useSearchProducts'
import CardCollectionSkeleton from '../../shared-components/CardCollectionSkeleton/CardCollectionSkeleton.vue'
import { textToTarget } from '../../utils/textToTarget'

import { ON_RETRY_INJECTION_KEY } from './retry/on-retry.injection-key'

export type RefinedProductsBlockProps = RefinedProductsApiProps &
  BlockProps & { clientOnly?: boolean }

const props = withDefaults(defineProps<RefinedProductsBlockProps>(), {
  ctaLink: undefined,
})

// This event is common to every CMS block in order to hide the block containers
// on demand
const emits = defineEmits(['error', 'reset-error', 'loaded'])

const {
  data: products,
  error,
  status,
  pending,
  clear,
  refresh,
} = useSearchProducts(
  {
    productQuery: props.productQuery,
  },
  {
    immediate: true,
    server: !props.clientOnly,
    productGranularity: props.productGranularity || undefined,
  },
)

const registerRetryFn = inject(ON_RETRY_INJECTION_KEY, undefined)

registerRetryFn?.(() => {
  clear()
  refresh()
})

useAddProductsCacheTagHeader({ products })

const layout = computed(() =>
  props.productLayout === 'grid' ? 'grid' : 'carousel',
)

watch(
  products,
  () => {
    if (status?.value === 'pending' || status?.value === 'idle') return

    if (!products.value || products.value.length === 0) {
      emits('error')

      return
    }
    emits('reset-error')
  },
  { immediate: true },
)

watch(
  status,
  (st) => {
    if (st === 'success') {
      emits('loaded', products.value)
    }
  },
  { immediate: true },
)

watch(
  error,
  () => {
    if (error.value) {
      emits('error')
    }
  },
  { immediate: true },
)

const hasProducts = computed(() => products.value && products.value.length > 0)
</script>
